/**
 * projectspine.mjs — the learner's own project, carried across a ladder.
 *
 * FOUNDER, 2026-08-15: "all ai courses were supposed to have a user own project along with
 * evaluating ankr products" → "build p1".
 *
 * ── THIS IS CAPTURE, NOT AUTHORSHIP ───────────────────────────────────────────────────────────
 * Code101 and CodeAI101 already carry a personal project across fourteen days through 28 paired
 * conversations, and the CodeAI101 capstone enumerates TWELVE decisions the learner is supposed to
 * have made by the end. That pedagogy is written. Nothing held it — every paired conversation began
 * by asking the learner to paste their project in again, because there was nowhere for it to live.
 *
 * This module is that somewhere. It invents no requirement; it makes an existing one persistent.
 *
 * ── VERIFIED BEFORE BUILT (VIVECHANA-014) ─────────────────────────────────────────────────────
 * The format spec's Vivechana rested on Rep=8ᴱ — one capture mechanism auto-applying to any ladder
 * that declares a project. That was the load-bearing estimate and the spec made verifying it task
 * one. Drafted for two deliberately unlike ladders: CODEAI101 (a software design doc) and 2M (a
 * passage plan). Result: ONE slot shape, `{chapter, id, asks}`, carries both. Rep=8 holds and the
 * build proceeded on a verified estimate rather than a hopeful one.
 *
 * ── PURE, SO THE GATE TESTS THE CODE THE BROWSER RUNS ─────────────────────────────────────────
 * No React, no browser APIs, no storage. Everything takes the declaration as an argument. A gate
 * that reimplements a rule in order to check it is checking a copy — that lesson was paid for in
 * labrules.mjs and is not being re-learned here.
 */

/** The manifest row for a level, or null. */
export function ladderFor(decl, level) {
  const lvl = String(level || '').toUpperCase();
  return (decl?.ladders || []).find((l) => String(l.level).toUpperCase() === lvl) || null;
}

/**
 * Does this ladder carry a project?
 *
 * FAILS CLOSED. An unknown level, a missing manifest, or a row that forgot to say returns FALSE —
 * so a ladder gets a project spine only by being declared, never by being overlooked. The opposite
 * default would silently offer half a feature on ladders nobody had thought about.
 */
export const carriesProject = (decl, level) => ladderFor(decl, level)?.carries_project === true;

/** The declared slots, in chapter order. Empty for a ladder that carries no project. */
export function slotsFor(decl, level) {
  const l = ladderFor(decl, level);
  if (!l || l.carries_project !== true) return [];
  return [...(l.slots || [])].sort((a, b) => a.chapter - b.chapter);
}

export const slotAt = (decl, level, chapter) =>
  slotsFor(decl, level).find((s) => Number(s.chapter) === Number(chapter)) || null;

/**
 * A ladder may declare it CONTINUES another. CodeAI101 continues Code101 — the same project,
 * further on — which is why its capstone asks for decisions made on both.
 *
 * Returns the whole chain of levels whose slots belong to one project, oldest first.
 */
export function chainFor(decl, level) {
  /* Walk BACKWARDS along continues_from, then reverse once. The first version reversed inside the
     recursion and again on return, so a two-link chain came back newest-first — CODEAI101 → CODE101
     — which put the missing decisions in the opposite order to the one a learner works them in.
     A cycle guard is here because continues_from is hand-declared and two ladders pointing at each
     other would otherwise hang the page rather than report a bad manifest. */
  const back = [];
  let cur = ladderFor(decl, level);
  while (cur && !back.includes(cur.level)) {
    back.push(cur.level);
    cur = cur.continues_from ? ladderFor(decl, cur.continues_from) : null;
  }
  return back.reverse();
}

/**
 * What the learner has filled in, and what is still open.
 *
 * `project.slots` is a flat map of slotId → { value, filled_on }. Slot ids are unique across the
 * chain by construction (they are the founder's own phrases), so one map serves the whole project.
 */
export function progressOf(decl, level, project = {}) {
  const chain = chainFor(decl, level);
  const all = chain.flatMap((lvl) => slotsFor(decl, lvl).map((s) => ({ ...s, level: lvl })));
  const filled = all.filter((s) => {
    const v = project.slots?.[s.id]?.value;
    return typeof v === 'string' && v.trim().length > 0;
  });
  return {
    chain, all, filled,
    open: all.filter((s) => !filled.includes(s)),
    count: filled.length, total: all.length,
    /* Deliberately NOT a percentage or a score. The capstone asks whether every decision has been
       made, and "83% of a design" is not a thing that can be checked. */
    complete: all.length > 0 && filled.length === all.length,
  };
}

/**
 * THE PUBLISHED CHECK.
 *
 * The format spec's law: "The check that grades an artefact is PUBLISHED BEFORE the learner starts.
 * A rubric revealed after submission is an examiner's opinion; a rubric published in advance is a
 * specification."
 *
 * So this returns the same object whether or not anything has been submitted, and the surface shows
 * it from the first day. It reports WHICH decisions are missing by name, because "not yet" that
 * cannot say what is missing is a grade, not a check.
 */
export function checkArtefact(decl, level, project = {}) {
  const p = progressOf(decl, level, project);
  const l = ladderFor(decl, level);
  return {
    published_standard: p.all.map((s) => ({ id: s.id, asks: s.asks, chapter: s.chapter, level: s.level })),
    artefact: l?.artefact ?? null,
    passes: p.complete,
    missing: p.open.map((s) => ({ id: s.id, asks: s.asks, chapter: s.chapter, level: s.level })),
    /* No score, no percentage, no grade. Either every declared decision has been made or the
       specific ones outstanding are named. */
    verdict: p.total === 0 ? 'this ladder carries no project'
      : p.complete ? 'every declared decision is made'
        : `${p.open.length} of ${p.total} decisions still open`,
  };
}

/**
 * PRIVACY IS A PROPERTY OF THE SHAPE, NOT A PROMISE IN A DOCUMENT.
 *
 * A project is the learner's. This module never returns anyone else's, has no concept of browsing
 * projects, and exposes no aggregate. The only thing it will hand out about a project that is not
 * yours is that a check passed — which is what a certificate needs and is the least that can be
 * said. FP-001, and the privacy ANKR enforces it has to embody.
 */
export function publicSummary(decl, level, project = {}) {
  const c = checkArtefact(decl, level, project);
  return { level, passes: c.passes, decisions: c.published_standard.length };
}

/** The founder's own words for a slot, so no surface paraphrases the ask. */
export const askOf = (decl, level, slotId) =>
  chainFor(decl, level).flatMap((lvl) => slotsFor(decl, lvl)).find((s) => s.id === slotId)?.asks ?? null;
